import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import { SecurityProcessMenu } from '../components/SecurityProcessMenu.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import { isOtpLockedOut } from '../lib/action-denied.js';
import { beginHighSecurity, commitHighSecurity } from '../lib/high-security.js';
import { securityRunKey } from './SecurityScreen.js';
import { ECL_HIGH_RUN_SNAPSHOT_KEY } from '../security-run-snapshot.js';
import infoIcon from '../assets/info-icon.svg';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

type CheckStatus = 'pass' | 'fail' | null;

/**
 * Accessible pass/fail radio pair, faithful to the export's `AccessibleRadio`
 * (shared with the Medium / High / Outbound security ports).
 */
function PassFailRadios({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              onChange={() => onChange(option)}
              disabled={disabled}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** A labelled identity question with its value and pass/fail radios. */
function IdentityQuestion({
  label,
  value,
  name,
  status,
  onChange,
}: {
  label: string;
  value: string;
  name: string;
  status: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          {label}
        </h3>
        <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
          {value}
        </p>
      </div>
      <div className="ml-auto mt-5">
        <PassFailRadios name={name} value={status} onChange={onChange} />
      </div>
    </div>
  );
}

interface OtpGenerateResult {
  sent: boolean;
  resultCode?: string;
  maskedMobile?: string;
  attemptsRemaining?: number;
  blocked?: boolean;
  message?: string;
}

interface OtpValidateResult {
  valid: boolean;
  resultCode?: string;
  message?: string;
  attemptsRemaining?: number;
  expired?: boolean;
  blocked?: boolean;
}

/** Maximum OTP validation attempts within the 5-minute window (CCF-107/16). */
const MAX_OTP_ATTEMPTS = 3;

/**
 * The customer's identity-question data (`POST /api/security/questions`,
 * bank-gateway PascalCase) — only the fields the ECL High flow needs. The
 * gateway models this as an open passthrough object (`additionalProperties`),
 * so the SPA reads it through this LOCAL interface (no contract change).
 */
interface SecurityQuestionsData {
  MothersMaidenName?: string;
  RecurringPayment?: string;
  JointAccounts?: string[];
  // Issue #387: the customer's registered mobile number, sourced by the BFF
  // from `customer/info` (`getSecurityQuestions` enriches it there — the
  // mock-only `customer/security-questions` op does NOT carry it, #76/#281).
  // Displayed in the "Mobile number" section so the agent can confirm it with
  // the customer, instead of the old "Confirm with the customer" placeholder.
  // Rendered AS STORED — the mobile format convention (numeric vs E.164/`+`)
  // is #220's parked ruling and is NOT decided here.
  MobileNumber?: string;
  Landline?: string;
  EmailAddress?: string;
  ActiveVisaDebitCards?: string[];
  // ai-review HIGH (issue #345 follow-up): set by the BFF when the
  // `card/list` enrichment fails — an upstream/auth/gateway error is not
  // equivalent to "no active Visa debit card on file" and must fail closed
  // (excluded from the fallback question set), never silently render as an
  // authoritative empty list.
  ActiveVisaDebitCardsError?: boolean;
  // ai-review HIGH (issue #345 follow-up): set by the BFF when the
  // `customer/info` enrichment behind `EmailAddress` fails — same fail-open
  // class as `ActiveVisaDebitCardsError` above: an upstream/auth/JWS/
  // customer-info failure is not equivalent to an authoritative "no email
  // on file" and must fail closed (excluded from the fallback question set,
  // never silently rendered as an accepted absence).
  EmailAddressError?: boolean;
}

/**
 * One product/account row from `/security/products-summary` (issue #676 —
 * CCRS-3493; identical shape to EDS High's `SecurityScreen.tsx`). Always carries
 * `AccountLast4` (rows without at least 4 recoverable trailing digits are dropped
 * server-side); an ACCOUNT row uses the nickname/type, a non-account PRODUCT row
 * (Visa credit card / mortgage / insurance / investment — issue #604) carries
 * `ProductDescription`.
 */
interface ProductSummaryRow {
  AccountLast4: string;
  AccountType?: string;
  Nickname?: string;
  ProductDescription?: string;
}

/**
 * Formats one of the customer's ACTUAL other products/accounts for the
 * MMN-not-on-file fallback list (issue #676 — CCRS-3493; reuses EDS High's
 * `formatProductEntry`): the nickname when set, otherwise the product
 * description or account type, plus the last 4 digits (never the full number),
 * mirroring the joint-accounts confirmation string's style (e.g.
 * "Explore Account ••2381").
 */
function formatProductEntry(p: ProductSummaryRow): string {
  const desc =
    p.Nickname?.trim() ||
    p.ProductDescription?.trim() ||
    p.AccountType?.trim() ||
    'Account';
  return `${desc} ••${p.AccountLast4}`;
}

/**
 * Fetch/render state for the customer's ACTUAL other products/accounts (issue
 * #676, mirroring EDS High). `'idle'` = not yet asked (the maiden "not on file"
 * tick is not set); `'loading'` = the authoritative fetch is in flight;
 * `'error'` = the fetch failed or the BFF returned non-OK — deliberately
 * DISTINCT from an authoritative empty list, so the screen never silently
 * degrades to a weaker fallback the agent could rubber-stamp a Pass against; an
 * array (possibly empty) = the authoritative result loaded.
 */
type ProductsSummaryState = 'idle' | 'loading' | 'error' | ProductSummaryRow[];

/** Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY` for display. */
function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not available';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

/** Accessible dropdown for the active Visa debit cards (faithful to the export). */
function CardDropdown({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div
      className="flex flex-col gap-2"
      ref={ref}
      style={{ maxWidth: '260px' }}
    >
      <label
        htmlFor="ecl-visa-card"
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        Active Visa debit card numbers
      </label>
      <div className="relative">
        <button
          id="ecl-visa-card"
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={value ? '' : 'text-[#6b7280]'}>
            {/* #527/CCF-107: render the FULL 16-digit Visa debit card number — do NOT mask. */}
            {value ? value : 'Active Visa cards numbers'}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && options.length > 0 && (
          <ul
            role="listbox"
            aria-label="Active Visa debit card numbers"
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((card) => (
              <li
                key={card}
                role="option"
                aria-selected={value === card}
                tabIndex={0}
                onClick={() => {
                  onChange(card);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(card);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === card ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {card}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The three ordered ECL fallback questions (CCF-107). */
type FallbackKey = 'landline' | 'email' | 'visa';

/** This screen's RBAC screen id + route, used to key/restore the in-progress
 * check-state snapshot (issue #345, mirroring #344's EDS High mechanism). */
const ECL_HIGH_SCREEN_ID = 'SCRN-55';
const ECL_HIGH_ROUTE = '/security/ecl-high';

/**
 * sessionStorage key for the in-progress ECL-High check snapshot (issue #345,
 * mirroring `SecurityScreen.tsx`'s issue-#344 mechanism for EDS High).
 * "Check Transactions" (the recurring-payment question, dylan219's #344
 * ruling applied here too) navigates the agent to the Transactions screen in
 * the SAME tab, unmounting this component and losing its local `useState`.
 * This snapshot preserves the agent's Pass/Fail selections, "not on file"
 * ticks, 2FA/OTP progress and fallback progress across that round trip, so
 * returning via the Transactions screen's "Back to High Security" button
 * resumes exactly where the agent left off. Bound to the same `securityRunKey`
 * (customer + screen id) `SecurityScreen.tsx` already uses — reusing the
 * existing fail-closed pattern rather than a parallel key scheme — so a
 * snapshot can never be applied to the wrong customer.
 *
 * The key itself lives in `../security-run-snapshot.js` (imported above) so
 * App's Clear Session (#780) shares the exact literal and the two can never
 * drift apart.
 */

interface EclHighRunSnapshot {
  runKey: string;
  fullNameStatus: CheckStatus;
  dobStatus: CheckStatus;
  mmnStatus: CheckStatus;
  mobileStatus: CheckStatus;
  recurringStatus: CheckStatus;
  jointStatus: CheckStatus;
  mmnNotOnFile: boolean;
  mobileNotOnFile: boolean;
  otpSent: boolean;
  maskedMobile: string;
  // otpCode is deliberately NOT persisted (ai-review MEDIUM, issue #345): a
  // one-time passcode is a security credential and must not be written to
  // sessionStorage — the OTP input restores empty and the agent re-enters it.
  otpStatus: CheckStatus;
  otpAttemptsRemaining: number;
  otpExpired: boolean;
  otpBlocked: boolean;
  unableTo2fa: boolean;
  fallbackStatus: Record<FallbackKey, CheckStatus>;
  selectedCard: string;
  // #840: the overall identity-question result is recomputed LIVE from the
  // persisted statuses on restore (no latched `runFailed` snapshot field) — a
  // mis-clicked question Fail stays correctable to Pass across the round trip.
  // Fallback answers are live too while the fallback section is in use. Issue
  // #405 is the one (narrowed) carve-out: UNTICKING "Unable to complete 2FA"
  // while a fallback answer is Fail latches `failedViaFallback`, which must
  // survive the Check-Transactions round trip so the OTP path (which unmounts the
  // failed fallback question) cannot rescue it.
  failedViaFallback: boolean;
}

/**
 * Loads the in-progress snapshot, only when it belongs to the SAME customer
 * (`runKey`) — a snapshot for a different run is stale and ignored
 * (fail-closed, mirrors `verificationStateMatchesCustomer`).
 */
function loadEclHighRunSnapshot(runKey: string): EclHighRunSnapshot | null {
  try {
    const raw = sessionStorage.getItem(ECL_HIGH_RUN_SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as EclHighRunSnapshot;
    return snap.runKey === runKey ? snap : null;
  } catch {
    return null;
  }
}

function saveEclHighRunSnapshot(snapshot: EclHighRunSnapshot): void {
  try {
    sessionStorage.setItem(ECL_HIGH_RUN_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // Non-fatal: storage may be unavailable (private mode) — the round trip
    // still works, the agent just resumes with a clean run.
  }
}

function clearEclHighRunSnapshot(): void {
  try {
    sessionStorage.removeItem(ECL_HIGH_RUN_SNAPSHOT_KEY);
  } catch {
    // Non-fatal.
  }
}

/**
 * SCRN 55 — ECL High Security (CCF-107).
 *
 * Faithful port of the customer's Figma "update high risk ECL" export. An
 * Enhanced Customer Support Agent (ECSA) authenticates a vulnerable, HIGH-risk
 * customer with the full ECL identity suite — a SUPERSET of the ECL Medium
 * (SCRN-54) check.
 *
 * Mandatory identity questions (all must Pass): Full name, Date of birth,
 * Mother's maiden name (if not on file → "Do you have any other products or
 * accounts with PTSB?" with the related products listed), Mobile number (if not
 * on file → a note to ensure the alternative/fallback questions are completed),
 * Recurring outgoing payments (Yes → confirm one payment name + frequency; No →
 * accepted), and Joint accounts with PTSB (Yes → confirm the joint holder; No →
 * accepted). All carry a Pass/Fail.
 *
 * 2FA: a 6-digit OTP to the registered mobile (5-minute expiry, 3 attempts then
 * security fails + block for 24h, expiry allows a new code) — identical to the
 * Medium screen.
 *
 * Fallback ("Unable to complete 2FA"): the three additional questions worked in
 * the FIXED order — landline, email, then the active-Visa-debit-card catch-all.
 * The threshold depends on whether the mobile is on file: mobile on file AND
 * confirmed → **1** correct answer passes; mobile NOT on file → **2** correct
 * answers are required. Blank/unavailable questions are skipped to the next; an
 * incorrect (Fail) answer fails authentication terminally. If there is
 * insufficient data to meet the 1/2 requirement the customer must visit a
 * branch with photo ID.
 *
 * Architecture: this is a SIBLING of the Medium / EDS / VISA security screens
 * and REUSES their existing Teller-ID-gated BFF routes — `/security/otp/generate`
 * + `/security/otp/validate` (2FA) and `/security/questions` (mother's maiden
 * name / recurring payment / joint accounts AND the landline / email / active
 * Visa debit cards for the fallback). It adds NO new client↔BFF or BFF↔gateway
 * routes. Name + DoB are read from the verified-customer context. Completion
 * flips `securityLevel` to `'high'`, unlocking the Customer Dashboard.
 */
export function EclHighSecurityScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, updateCustomer } = useCustomer();

  // Admin-editable scripting text (content-catalogue keys); falls back to the
  // compiled-in defaults. Rendered as PLAIN TEXT only (no HTML).
  const eclHighFailAdvisory = useContent('ecl.high.security.fail.advisory');
  const eclHighFailRemark = useContent('ecl.high.security.fail.remark');

  const customerNumber = customer.cif ?? '';

  // Issue #345 (mirroring #344): restore the in-progress check state ONLY when
  // arriving back via the Transactions screen's "Back to High Security"
  // button, which sets `state.restoreSecurityRun`. Any other entry (fresh
  // navigation, the process menu, a direct link) leaves it unset, so no
  // snapshot is applied and the screen starts clean. Computed once at mount —
  // read into the `useState` initial values below.
  const restoredSecurity = useRef<EclHighRunSnapshot | null>(
    (location.state as { restoreSecurityRun?: boolean } | null)
      ?.restoreSecurityRun
      ? loadEclHighRunSnapshot(
          securityRunKey(customerNumber, ECL_HIGH_SCREEN_ID),
        )
      : null,
  );

  const [fullNameStatus, setFullNameStatus] = useState<CheckStatus>(
    restoredSecurity.current?.fullNameStatus ?? null,
  );
  const [dobStatus, setDobStatus] = useState<CheckStatus>(
    restoredSecurity.current?.dobStatus ?? null,
  );
  const [mmnStatus, setMmnStatus] = useState<CheckStatus>(
    restoredSecurity.current?.mmnStatus ?? null,
  );
  const [mobileStatus, setMobileStatus] = useState<CheckStatus>(
    restoredSecurity.current?.mobileStatus ?? null,
  );
  const [recurringStatus, setRecurringStatus] = useState<CheckStatus>(
    restoredSecurity.current?.recurringStatus ?? null,
  );
  const [jointStatus, setJointStatus] = useState<CheckStatus>(
    restoredSecurity.current?.jointStatus ?? null,
  );
  // Issue #345 (ai-review HIGH): a failed/non-OK authoritative joint-accounts
  // lookup must NOT be indistinguishable from a genuinely-empty result — an
  // upstream/auth/gateway error is not equivalent to "no joint accounts on
  // file" and must fail closed (block Pass), not silently degrade to it.
  const [jointAccountsError, setJointAccountsError] = useState(false);
  // Issue #345 (ai-review HIGH, follow-up finding): the lookup is
  // asynchronous, so — before it settles — `jointAccountsError` is false AND
  // `questionData.JointAccounts` is empty, which rendered exactly like an
  // AUTHORITATIVE "no joint accounts" result with Pass/Fail already enabled
  // (a fast agent could mark Pass before the lookup ever ran). Track whether
  // the lookup has settled (success OR error) at all, so the question can
  // fail closed — Pass/Fail disabled, a "checking…" state shown — for the
  // ENTIRE pending window, not just the failed one.
  const [jointAccountsResolved, setJointAccountsResolved] = useState(false);

  // "Not on file" ticks (CCF-107) — exclude the field from the mandatory checks
  // and reveal the corresponding guidance.
  const [mmnNotOnFile, setMmnNotOnFile] = useState(
    restoredSecurity.current?.mmnNotOnFile ?? false,
  );
  const [mobileNotOnFile, setMobileNotOnFile] = useState(
    restoredSecurity.current?.mobileNotOnFile ?? false,
  );

  // #676 (CCRS-3493): the customer's ACTUAL other products/accounts for the
  // maiden-name-not-on-file "Do you have any other products or accounts with
  // PTSB?" fallback — sourced from `/security/products-summary` (the same real
  // `customer/accounts/list` + `customer/products/list` gateway op EDS High
  // already uses, no new gateway call), instead of the old hard-coded generic
  // category list. Fetched only once the maiden "not on file" branch is ticked.
  const [otherProducts, setOtherProducts] =
    useState<ProductsSummaryState>('idle');
  // Records which context the CURRENTLY-held `otherProducts` value was fetched
  // for, so a stale result from a prior customer/Teller can never be read back
  // as this context's authoritative list (mirrors EDS High's guard).
  const otherProductsContextRef = useRef<{
    customerNumber: string;
    tellerId: string;
  } | null>(null);

  // The identity-question data (mother's maiden name / recurring payment / joint
  // accounts + the fallback landline / email / active Visa debit cards). Loaded
  // up-front (once ID&V is done) so the mandatory high-risk questions can show
  // their on-file values without waiting for the fallback opt-in.
  const [questionData, setQuestionData] = useState<SecurityQuestionsData>({});
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  // The context key the questions were last loaded for (CCF-112, #477). Kept in
  // state — rather than relying on the bare `questionsLoaded` flag — so we can
  // derive purely from props whether the loaded data belongs to the CURRENT
  // customer/Teller context. This backs the stale-outcome guard on the terminal
  // Security Result event below: on a context swap it no longer matches the new
  // context until THIS customer's own /security/questions load re-stamps it.
  const [questionsContext, setQuestionsContext] = useState<string | null>(null);

  // 2FA OTP state (reuses the SCRN-03 endpoints + CCF-16 lifecycle).
  const [otpSent, setOtpSent] = useState(
    restoredSecurity.current?.otpSent ?? false,
  );
  const [maskedMobile, setMaskedMobile] = useState(
    restoredSecurity.current?.maskedMobile ?? '',
  );
  const [generating, setGenerating] = useState(false);
  // Never restored from the snapshot (ai-review MEDIUM, issue #345) — a
  // one-time passcode is a security credential and must not persist in
  // sessionStorage; the agent re-enters it after a same-tab round trip.
  const [otpCode, setOtpCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'pass' | 'fail' | null>(
    restoredSecurity.current?.otpStatus ?? null,
  );
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(
    restoredSecurity.current?.otpAttemptsRemaining ?? MAX_OTP_ATTEMPTS,
  );
  const [otpExpired, setOtpExpired] = useState(
    restoredSecurity.current?.otpExpired ?? false,
  );
  const [otpBlocked, setOtpBlocked] = useState(
    restoredSecurity.current?.otpBlocked ?? false,
  );
  // Non-blocking inline warning for a wrong (non-final) OTP code (issue #782,
  // per @dylan219's UX rule #80/#82/#94 — mirrors EclMediumSecurityScreen): the
  // gateway code + message are surfaced INLINE under the Send Code button so the
  // agent can re-enter the code without leaving the screen, in addition to the
  // existing transient toast. The expired / blocked branches keep their own
  // dedicated alerts.
  const [otpError, setOtpError] = useState<{
    code?: string;
    message: string;
    // Which action produced the error, so it renders directly under THAT
    // button (#782): 'generate' → under Send Code; 'validate' → under Confirm.
    source: 'generate' | 'validate';
  } | null>(null);
  // #810: the gateway's (BFF-mapped, ruled #810 copy) message for the expired
  // (2004) state, rendered inline in the dedicated branch alert when present.
  const [otpExpiredMessage, setOtpExpiredMessage] = useState<string | null>(
    null,
  );

  // Fallback ("Unable to complete 2FA") state.
  const [unableTo2fa, setUnableTo2fa] = useState(
    restoredSecurity.current?.unableTo2fa ?? false,
  );
  const [fallbackStatus, setFallbackStatus] = useState<
    Record<FallbackKey, CheckStatus>
  >(
    restoredSecurity.current?.fallbackStatus ?? {
      landline: null,
      email: null,
      visa: null,
    },
  );
  const [selectedCard, setSelectedCard] = useState(
    restoredSecurity.current?.selectedCard ?? '',
  );

  const tellerId = customer.tellerId ?? '';
  const dobDisplay = formatDob(customer.dob);
  const hasDob = Boolean(customer.dob);
  const fullName = (customer.name ?? '').trim();
  const hasName = fullName.length > 0;
  // #565 (Figma 2FA-script alignment): greet by first name in the 2FA read-back,
  // derived the same way the Medium/High Security screens do.
  const firstName = fullName.split(/\s+/)[0] ?? '';
  // ECL High gates its data loads + Send Code + upgrade on `customerNumber &&
  // tellerId` only — identical to the shared SecurityScreen (EDS/VISA/Business/
  // Mortgage High) — so a customer reached via Customer Search (identified at
  // level `none`, e.g. a Unibanks-only customer) renders the identity questions
  // and can run 2FA, exactly like the other high-security screens (#787, CCF-107;
  // accepted under #650/#609). It does NOT add an extra
  // `customerVerification === 'Y'` (prior-ID&V) precondition — that was
  // client-side defence-in-depth only; the BFF remains the enforcing boundary
  // (OTP/session-card/RBAC + fail-closed High elevation, #363).

  // High-risk identity-question data sourced from /security/questions. Each is
  // "on file" only when present + non-blank — a missing value cannot be marked
  // Pass (parity with Name/DoB; marking Pass on absent data must not verify).
  const mothersMaidenName = (questionData.MothersMaidenName ?? '').trim();
  const recurringPayment = (questionData.RecurringPayment ?? '').trim();
  const jointAccounts = questionData.JointAccounts ?? [];
  const hasMothersMaidenName = mothersMaidenName.length > 0;
  const hasJointAccounts = jointAccounts.length > 0;
  // #817 (@dylan219, authoritative — supersedes the #674/#660 N/A treatment):
  // the joint-accounts question is now ALWAYS answerable. When the authoritative
  // `customer/joint-associations` lookup has resolved with NO joint accounts the
  // screen shows "No joint accounts on file." (below) and the agent must STILL
  // mark Pass/Fail — matching the registration screens. Fail-closed is preserved
  // by `jointOk` below (an errored or not-yet-settled lookup still blocks Pass).

  // Issue #387: the customer's registered mobile number from `customer/info`
  // (BFF-enriched onto the security-questions payload). Shown in the Mobile
  // number section so the agent confirms the real number with the customer,
  // not the old "Confirm with the customer" placeholder. Rendered verbatim —
  // no reformatting (mobile FORMAT is #220's parked ruling). This is a DISPLAY
  // aid only; it is NOT the authoritative on-file evidence that relaxes the
  // CCF-107 fallback threshold — that remains the masked mobile surfaced by the
  // gateway OTP-generate response (`maskedMobile`), see below.
  const mobile = (questionData.MobileNumber ?? '').trim();
  const landline = (questionData.Landline ?? '').trim();
  const email = (questionData.EmailAddress ?? '').trim();
  const activeCards = questionData.ActiveVisaDebitCards ?? [];
  // ai-review HIGH (issue #345 follow-up): a failed `card/list` enrichment is
  // NOT equivalent to an authoritative "no active Visa debit card" — fail
  // closed by excluding the Visa question from the fallback set entirely
  // (below) rather than silently accepting "no card" as the correct answer.
  const activeVisaCardsError = Boolean(questionData.ActiveVisaDebitCardsError);
  // ai-review HIGH (issue #345 follow-up): mirrors the Visa handling above —
  // a failed `customer/info` enrichment behind `EmailAddress` is NOT
  // equivalent to an authoritative "no email on file"; fail closed by
  // excluding the email question from the fallback set (below) rather than
  // silently treating the error as an accepted absence.
  const emailAddressError = Boolean(questionData.EmailAddressError);

  // CCF-107: a fallback question can only be answered when its data is actually
  // on file (an authoritative /security/questions response). A field that is
  // blank/absent — including before the questions call returns — is "not on
  // file" and CANNOT be marked Pass. Visa is the catch-all final fallback and is
  // ALWAYS answerable once the data has loaded ("If no card — this is the correct
  // answer"); gating it on having a card would make a no-card customer
  // impossible to authenticate.
  const landlineAvailable = landline.length > 0;
  // Never available when the underlying lookup errored (ai-review HIGH,
  // #345 follow-up) — an error must never be treated as an authoritative
  // "no email on file", so it is excluded from the fallback set below rather
  // than silently substituted with a different question.
  const emailAvailable = email.length > 0 && !emailAddressError;
  const hasCardOnFile = activeCards.length > 0;

  // CCF-107: the mobile-on-file (and confirmed Pass) state sets the fallback
  // threshold — 1 correct answer when the mobile is on file + confirmed, 2 when
  // the mobile is NOT on file. The relaxed 1-question threshold may ONLY be used
  // with AUTHORITATIVE evidence that a mobile is on file, NOT the agent's word
  // alone. Two gateway-sourced signals count as that evidence:
  //   1. the authoritative on-file mobile from `customer/info` (`mobile`, added
  //      by #387) — present WITHOUT generating an OTP; or
  //   2. the masked mobile surfaced by the gateway OTP-generate response
  //      (`maskedMobile`) — present only after the agent clicks "Send code".
  // Issue #391 (@dylan219 ruled option A): the customer is often "unable to
  // complete 2FA", so the agent ticks that and never generates an OTP. Before
  // this change the threshold keyed ONLY on `maskedMobile`, so that natural flow
  // never unlocked the 1-question path even though `customer/info` holds an
  // authoritative on-file mobile — and #398 already trusts that same
  // `customer/info` mobile on the other HIGH screens. Recognising it here (in
  // ADDITION to `maskedMobile`) fixes the inconsistency. This is still
  // gateway-sourced and still requires an explicit agent Pass, so it is NOT an
  // agent free-pass on an unverifiable claim.
  const mobileOnFile = mobile.length > 0 || maskedMobile.length > 0;
  const mobileOnFileAndConfirmed =
    !mobileNotOnFile && mobileStatus === 'pass' && mobileOnFile;
  const requiredFallbackAnswers = mobileOnFileAndConfirmed ? 1 : 2;

  // The fixed-order list of fallback questions that are actually answerable
  // (have data on file); Visa is appended as the always-answerable catch-all
  // once the authoritative data has loaded. The agent must answer them IN ORDER:
  // only the first `requiredFallbackAnswers` un-skipped questions are offered, so
  // an agent can't skip an available landline/email to cherry-pick a later one.
  const orderedAvailableFallback: FallbackKey[] = [];
  if (landlineAvailable) orderedAvailableFallback.push('landline');
  if (emailAvailable) orderedAvailableFallback.push('email');
  // Issue #345 (ai-review HIGH follow-up): excluded when the card/list lookup
  // errored — an unresolved/failed check can never count toward the fallback
  // threshold (fail-closed, no-bypass).
  if (questionsLoaded && !activeVisaCardsError)
    orderedAvailableFallback.push('visa');

  // The questions that count toward the threshold: the first N available ones in
  // fixed order. A question is "active" (answerable) iff it is in this slice.
  const activeFallbackKeys = orderedAvailableFallback.slice(
    0,
    requiredFallbackAnswers,
  );
  const isFallbackActive = (key: FallbackKey): boolean =>
    activeFallbackKeys.includes(key);

  // When the Visa catch-all is among the active questions AND a card is on file,
  // the agent must first pick WHICH card they confirmed against — a bare "Pass"
  // with no selected card is not a complete confirmation (CCF-107). With no card
  // on file there is nothing to select ("no" is the correct answer).
  const visaSelectionOk =
    !activeFallbackKeys.includes('visa') ||
    !hasCardOnFile ||
    selectedCard !== '';

  // CCF-107: there must be enough answerable questions to MEET the threshold —
  // otherwise there is insufficient data and the customer must visit a branch.
  const enoughFallbackData =
    questionsLoaded && activeFallbackKeys.length >= requiredFallbackAnswers;

  // The fallback passes only when EVERY required (active) question is answered
  // Pass and (for the Visa catch-all) a card is selected when one is on file.
  const fallbackPassed =
    unableTo2fa &&
    enoughFallbackData &&
    activeFallbackKeys.every((k) => fallbackStatus[k] === 'pass') &&
    visaSelectionOk;
  // Any available fallback question answered Fail fails authentication — this
  // covers supplementary questions (beyond the required threshold) too, so a
  // wrong Visa answer is never silently ignored even when Visa is not in the
  // required activeFallbackKeys slice (issue #708).
  const anyFallbackFailed = orderedAvailableFallback.some(
    (k) => fallbackStatus[k] === 'fail',
  );

  // The second factor is satisfied by EITHER a passing 2FA OTP OR — via the
  // fallback path — the required number of correctly answered fallback questions.
  const secondFactorOk = unableTo2fa ? fallbackPassed : otpStatus === 'pass';

  // The "not on file" branches only LEGITIMATELY apply when the authoritative
  // /security/questions data has loaded AND genuinely has no value for that field
  // #626 (@dylan219, reversing #348 for maiden): the MAIDEN "not on file" tick is
  // now ALWAYS a valid branch, even when a maiden name IS on file — PTSB branches
  // enter junk ('unknown','TBC','Not known'…) into the maiden field that can't be
  // pattern-matched, so the agent must always be able to record it not-on-file and
  // verify against the customer's other products instead. This deliberately
  // re-opens the maiden bypass #348 closed (confirmed acceptable given the junk
  // data; the agent decides when to use it).
  // #701 (mirrors SecurityScreen.tsx #662): MOBILE also allows the tick when mobile IS on
  // file — the no-bypass invariant is preserved by `requiredFallbackAnswers` being 2.
  const mmnNotOnFileValid = mmnNotOnFile && questionsLoaded;
  // #701 (mirrors SecurityScreen.tsx #662 / CCRS-3447): the mobile "not on file" tick
  // is always a valid branch when checked — the no-bypass invariant is preserved because
  // `mobileOk` via the tick only satisfies the mobile question; `requiredFallbackAnswers`
  // still becomes 2 (vs 1 when the mobile is confirmed Pass), so the agent must answer 2
  // alternative questions explicitly. This reverses the #677/#348 `!mobileOnFile`
  // restriction on this screen.
  const mobileNotOnFileValid = mobileNotOnFile && questionsLoaded;

  // ai-review MEDIUM (#676): the render path must only treat a RESOLVED products
  // array as authoritative when it was fetched for the CURRENT customer/Teller
  // context. `otherProductsContextRef` records that context; if it does not match
  // (e.g. a prior customer's list is still held until the async context-reset
  // effect runs) fall through to the loading state rather than reading back a
  // stale cross-customer product list. Mirrors EDS High's `notOnFileFallback`
  // guard, but applied to the render itself.
  const otherProductsCtx = otherProductsContextRef.current;
  const otherProductsForContext: ProductsSummaryState = Array.isArray(
    otherProducts,
  )
    ? otherProductsCtx !== null &&
      otherProductsCtx.customerNumber === customerNumber &&
      otherProductsCtx.tellerId === tellerId
      ? otherProducts
      : 'loading'
    : otherProducts;

  // Each mandatory high-risk identity question passes when it is marked Pass AND
  // the data is actually on file — except when the field is legitimately not on
  // file (validated above), in which case the field is excluded from the
  // mandatory checks (its verification is deferred to the alternative/fallback
  // questions per CCF-107).
  // ai-review HIGH (#676): when the maiden-name "not on file" branch is taken,
  // the fallback "Do you have any other products or accounts with PTSB?" check
  // depends on the authoritative `/security/products-summary` list. That verdict
  // is derived from a gateway read, so it must FAIL CLOSED: the not-on-file
  // branch may only satisfy `mmnOk` when the products list resolved to an
  // authoritative array FOR THE CURRENT customer/Teller context. `loading`,
  // `error`, and `ProductsUnavailable: true` (all collapse to a non-array
  // `otherProductsForContext`) keep Pass/Continue blocked — the agent can never
  // complete High Security against a products list that could not be retrieved
  // (mirrors EDS High #346/#347 and the joint-accounts fail-closed rule above).
  const otherProductsAuthoritative = Array.isArray(otherProductsForContext);
  // ai-review HIGH (#702): when `mmnNotOnFile` is ticked the agent is taking the
  // "verify against other products" path — `hasMothersMaidenName` must NOT
  // short-circuit that check, because the not-on-file branch requires products to
  // be authoritative before Pass can count.  A stale normal-path Pass (set before
  // the checkbox was ticked) is cleared by the checkbox's onChange reset, but we
  // also gate `mmnOk` on the active branch so that a Pass selected while products
  // are still loading never satisfies the not-on-file path.
  const mmnOk =
    mmnStatus === 'pass' &&
    (mmnNotOnFile
      ? mmnNotOnFileValid && otherProductsAuthoritative
      : hasMothersMaidenName);
  // Recurring payments: Yes → the agent confirms one payment (Pass); No →
  // accepted as correct (also Pass). Either way the agent records Pass/Fail; the
  // mock always supplies a value, but treat an absent value as not-verifiable.
  const recurringOk = recurringStatus === 'pass';
  // Joint accounts: Yes → confirm the holder name (Pass); No → accepted (Pass).
  // Fails CLOSED when the authoritative lookup errored OR has not yet settled
  // (issue #345, ai-review HIGH + follow-up finding) — an upstream failure OR
  // a still-pending lookup must never be treated as an accepted Pass.
  // #817: even a genuinely-empty result requires the agent's explicit Pass (no
  // more N/A auto-satisfy). The settled + non-errored gate keeps it fail-closed:
  // the agent can only Pass once the read has resolved without error.
  const jointOk =
    jointStatus === 'pass' && jointAccountsResolved && !jointAccountsError;
  // Mobile number (issue #677, CCRS-3499 — refining #345 per the figma): when
  // "not on file" is ticked the mobile question is EXCLUDED from the mandatory
  // checks (its Pass/Fail radios are hidden) and verification defers to the
  // fallback questions, whose pass threshold `mobileNotOnFileValid` already
  // raises to 2. This is NOT a silent bypass — the customer must still satisfy
  // the (now 2) alternative questions below. Otherwise (mobile on file) the
  // agent must explicitly mark Pass on the mobile control.
  const mobileOk = mobileNotOnFileValid || mobileStatus === 'pass';

  // Any identity Fail, a failed (3-attempt-exhausted) OTP, or any incorrect
  // fallback answer surfaces the branch-referral guidance (CCF-107 unhappy
  // path). An OTP lockout is TERMINAL and path-independent. A fallback answer
  // is LIVE while the fallback section is in use (#840 — Fail→Pass recovers);
  // only unticking the fallback while an answer is Fail latches it (#405).
  // #677: a "not on file" mobile has no Pass/Fail radios, so any stale
  // `mobileStatus` must not surface as a Fail (mirrors the mmn/joint branches).
  const mobileFailed = !mobileNotOnFileValid && mobileStatus === 'fail';
  const anyFailed =
    fullNameStatus === 'fail' ||
    dobStatus === 'fail' ||
    mmnStatus === 'fail' ||
    mobileFailed ||
    recurringStatus === 'fail' ||
    // #817: the joint-accounts question is always answerable, so an explicit
    // Fail always counts (no N/A carve-out).
    jointStatus === 'fail' ||
    otpStatus === 'fail' ||
    otpBlocked ||
    anyFallbackFailed;

  // #840: the overall IDENTITY-question result is computed LIVE from the current
  // Pass/Fail selections (via `anyFailed`) — a mis-clicked identity question Fail
  // can be corrected back to Pass and the overall verdict + downstream actions
  // follow (no one-way `runFailed` latch). A genuine 2FA OTP gateway lockout
  // stays TERMINAL because `anyFailed` includes `otpStatus === 'fail'` /
  // `otpBlocked`, server-side states a radio flip does NOT clear (and which are
  // themselves persisted across the round trip).

  // Issue #840 (narrowing #405): while the "Unable to complete 2FA" fallback
  // section is in use, its answers recompute LIVE like every other Pass/Fail —
  // a mis-clicked fallback Fail flipped back to Pass recovers the verdict (via
  // the live `anyFallbackFailed` inside `anyFailed`). The ONLY latch is the #405
  // escape: if the agent UNTICKS "Unable to complete 2FA" while a fallback answer
  // is Fail (switching to the OTP path, which unmounts the failed radio), that
  // failure persists and is explained — passing the OTP afterwards must not
  // rescue it. The latch is set in `handleUnableTo2faChange` (never by merely
  // answering Fail) and survives the same-tab Check-Transactions round trip.
  const [failedViaFallback, setFailedViaFallback] = useState(
    restoredSecurity.current?.failedViaFallback ?? false,
  );

  // #263: the FAIL verdict must not be PRESENTED until ALL required security
  // steps are complete — every mandatory high-risk identity question answered
  // (pass or fail, or legitimately excluded via a valid "not on file" branch)
  // AND the second factor at a terminal outcome. On the OTP path that is a
  // passing OTP or a terminal (3-attempts / blocked) failure; on the fallback
  // path it is every REQUIRED active fallback question answered. A step still
  // pending shows in-progress, never a premature Fail. The latched-failure logic
  // is unchanged; this only gates the FAIL badge + branch-referral presentation.
  const answered = (s: CheckStatus): boolean => s === 'pass' || s === 'fail';
  const identityAllAnswered =
    answered(fullNameStatus) &&
    answered(dobStatus) &&
    (mmnNotOnFileValid
      ? otherProductsAuthoritative && answered(mmnStatus)
      : answered(mmnStatus)) &&
    // #845: a valid "mobile number not on file" tick hides the mobile Pass/Fail
    // radios (#677), so it counts as answered here (mirrors `mobileOk`) — else
    // `allStepsComplete` can never be true on that path and a Fail never shows.
    (mobileNotOnFileValid || answered(mobileStatus)) &&
    answered(recurringStatus) &&
    // #817: the joint-accounts question must always be answered Pass/Fail,
    // even when the customer has none (no N/A auto-complete).
    answered(jointStatus);
  // Issue #849 (@dylan219, mirroring the #774 registration rule): a mobile-number
  // FAIL auto-fails the 2FA step — an OTP cannot go to a mobile the customer
  // could not confirm — so the second factor is RESOLVED (failed) and the Fail
  // verdict only waits for the other identity questions, like every other Fail.
  // Live (#840): flipping the mobile back to Pass makes 2FA outstanding again.
  const secondFactorComplete = mobileFailed
    ? true
    : unableTo2fa
      ? // A single failed fallback answer resolves (fails) the path — nothing more
        // to action. Otherwise the required active questions must all be answered.
        anyFallbackFailed ||
        (enoughFallbackData &&
          activeFallbackKeys.every((k) => answered(fallbackStatus[k])) &&
          visaSelectionOk)
      : otpStatus === 'pass' || otpStatus === 'fail' || otpBlocked;
  const allStepsComplete =
    questionsLoaded && identityAllAnswered && secondFactorComplete;
  // #840: driven by the LIVE `anyFailed` (not a latch) so correcting a
  // mis-clicked identity-question OR fallback-answer Fail back to Pass clears
  // the FAIL presentation; a genuine OTP lockout keeps it (included in
  // `anyFailed`), and a fallback Fail the agent escaped by UNTICKING "Unable to
  // complete 2FA" keeps it via the `failedViaFallback` latch (#405/#840).
  const showFail = (anyFailed || failedViaFallback) && allStepsComplete;

  // Verification requires ALL the mandatory identity checks AND the second factor
  // AND no failure this run AND a completed prior ID&V (the high upgrade can't
  // bootstrap an unverified customer). Name + DoB must additionally be on file.
  //
  // FAIL CLOSED on the security-question data: the high-risk identity questions
  // (mother's maiden name, recurring payment, joint accounts) are sourced from
  // the authoritative `/security/questions` response. If that call has not loaded
  // successfully (`questionsLoaded` is false — including a failed/empty response,
  // whose catch is non-fatal for rendering), the screen has no authoritative data
  // to verify against, so it must NOT allow an upgrade — otherwise an agent could
  // tick "not on file" + mark the remaining checks Pass and verify with no
  // banking-sourced data (CCF-107; no-fail-open invariant).
  const allChecksPass =
    questionsLoaded &&
    fullNameStatus === 'pass' &&
    hasName &&
    dobStatus === 'pass' &&
    hasDob &&
    mmnOk &&
    mobileOk &&
    recurringOk &&
    jointOk &&
    secondFactorOk &&
    !anyFailed &&
    // Issue #405/#840: a fallback Fail escaped by unticking "Unable to complete
    // 2FA" is never rescued by later passing the OTP path (latched), so it also
    // blocks the Pass/Continue.
    !failedViaFallback;

  // Whether the loaded question data belongs to the CURRENT customer/Teller
  // context (CCF-112, #477). Derived PURELY from props (`questionsContext` is
  // stamped only when THIS context's /security/questions load returns), so it
  // flips to `false` on the very first render after a context swap — before any
  // effect (including the reset effect above) re-runs — and only becomes `true`
  // again once the new customer's own data has loaded. This is the ECL analogue
  // of the shared Medium screen's `mobileForContext` guard.
  const questionsForContext =
    questionsLoaded && questionsContext === `${customerNumber}|${tellerId}`;

  function bankHeaderParams(): { header: { 'X-Teller-Id': string } } {
    return { header: { 'X-Teller-Id': tellerId } };
  }

  // CCF-112 (#477): record the ECL High screen's TERMINAL outcome (Passed /
  // Failed) as a Non-API business event in Call History — the SAME event the
  // shared Medium/High security screens already fire (level `high`). Both
  // outcomes are pure client-side screen states, so — mirroring the standard
  // variants — they are fired best-effort here, exactly once per terminal
  // outcome. A once-guard (keyed to the customer + Teller context) prevents a
  // re-fire on re-render and resets when the context changes, so a fresh
  // customer is recorded again. A Call-History write failure must NEVER break
  // the flow (`.catch`).
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422 bug class): on a
  // context swap the per-customer reset effect above and this effect run in the
  // SAME commit, so on that render `customerNumber` is already the NEW CIF while
  // `allChecksPass`/`showFail` still derive from the PREVIOUS customer's
  // not-yet-cleared statuses — posting then would write a false Security Result
  // against the wrong customer. `questionsForContext` is derived purely from
  // props and flips to `false` on the first render of a new context, staying
  // false until THIS customer's own /security/questions load re-stamps it, so a
  // terminal outcome can never be attributed to the wrong customer. (`allChecksPass`
  // already requires `questionsLoaded`, so this cannot suppress a genuine event.)
  const securityEventFiredRef = useRef<string | null>(null);
  const lastContextKey = `${customerNumber}|${tellerId}`;
  useEffect(() => {
    if (securityEventFiredRef.current === lastContextKey) return;
    if (!customerNumber || !questionsForContext) return;
    // Terminal Pass = all checks pass; terminal Fail = every step complete but
    // the run did not pass (`showFail`). Neither fires while a step is pending.
    const outcome: 'Passed' | 'Failed' | null = allChecksPass
      ? 'Passed'
      : showFail
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = lastContextKey;
    void api
      .POST('/calls/security-event', {
        params: bankHeaderParams(),
        body: { customerNumber, securityLevel: 'high', outcome },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // `bankHeaderParams`/`lastContextKey` close over `tellerId`, already part of
    // the context key; excluded to keep the effect keyed to the terminal-outcome
    // transition for the current customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecksPass, showFail, customerNumber, questionsForContext]);

  function ensureContext(): boolean {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      toast.error('No verified customer in context. Complete ID&V first.');
      return false;
    }
    return true;
  }

  // Reset ALL screen-local verification state whenever the identified customer
  // changes (or the context becomes unverified). Without this, a pass marked for
  // one customer — or loaded question data / a masked mobile / a 2FA pass —
  // could survive a context swap and be applied to a DIFFERENT customer (the
  // shared customer context is mutated in place). Clearing `questionsLoaded`
  // also re-arms the fail-closed gate until the new customer's authoritative
  // data has loaded. Keyed on the customer identifier.
  //
  // Issue #345: this effect fires on every mount, including the very FIRST one
  // — which would otherwise immediately clobber the state just restored from
  // `restoredSecurity` (seeded into the `useState` initial values above) back
  // to blank. `skipInitialReset` is a one-shot guard that skips exactly that
  // first run when there IS a restored snapshot to apply; any LATER genuine
  // customer/ID&V change still resets as before (mirrors `SecurityScreen.tsx`).
  const skipInitialReset = useRef(Boolean(restoredSecurity.current));
  useEffect(() => {
    if (skipInitialReset.current) {
      skipInitialReset.current = false;
      return;
    }
    setFullNameStatus(null);
    setDobStatus(null);
    setMmnStatus(null);
    setMobileStatus(null);
    setRecurringStatus(null);
    setJointStatus(null);
    setMmnNotOnFile(false);
    setMobileNotOnFile(false);
    // #676: drop any prior customer's resolved other-products list on a context
    // swap so it can never be read back against a different customer.
    setOtherProducts('idle');
    otherProductsContextRef.current = null;
    setQuestionData({});
    setQuestionsLoaded(false);
    setQuestionsContext(null);
    setOtpSent(false);
    setMaskedMobile('');
    setOtpCode('');
    setOtpStatus(null);
    setOtpAttemptsRemaining(MAX_OTP_ATTEMPTS);
    setOtpExpired(false);
    setOtpBlocked(false);
    setUnableTo2fa(false);
    setFallbackStatus({ landline: null, email: null, visa: null });
    setSelectedCard('');
    // #405: clear the fallback-failure latch for the new customer (a fresh run).
    setFailedViaFallback(false);
  }, [customerNumber]);

  // Issue #345: the restore flag is a strict one-shot — `BrowserRouter`
  // persists route state in `window.history.state`, which would otherwise
  // wrongly re-trigger a restore on a later reload of the same URL. Strip it
  // from the current history entry once read (mirrors `SecurityScreen.tsx`'s
  // identical #344 handling). A non-restoring mount also clears any stale
  // snapshot so it can't leak into an unrelated later run.
  useEffect(() => {
    if (!restoredSecurity.current) clearEclHighRunSnapshot();
    if (
      (location.state as { restoreSecurityRun?: boolean } | null)
        ?.restoreSecurityRun
    ) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Begin the server-side High Security RUN (issue #363) when this (always-high)
  // screen opens for an identified customer — the seam the BFF uses to bind the
  // 2FA step (OTP validate / sanctioned skip) to a genuine high-security flow,
  // so a bare medium ID&V session cannot elevate directly. Best-effort; the
  // server still fail-closes at completion if the run was not recorded.
  useEffect(() => {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    void beginHighSecurity(customerNumber, tellerId);
  }, [customerNumber, tellerId]);

  // Fetch the identity-question data (mother's maiden name / recurring payment /
  // joint accounts + the fallback landline / email / active Visa debit cards)
  // once we have a verified customer + Teller ID. Reuses the existing
  // `/security/questions` route. Loaded up-front (not just on fallback opt-in) so
  // the mandatory high-risk questions can show their on-file values. Gated on
  // `customerNumber && tellerId` only (mirrors the shared SecurityScreen, #787).
  useEffect(() => {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (!response.ok || !data) return;
        if (!cancelled) {
          // Mask card identifiers at INGESTION so a raw PAN never enters client
          // state (no-PAN-leak invariant, defence-in-depth): even if a
          // misconfigured BFF/gateway returns full numbers, only the masked
          // `•••• 1234` form is ever held in `questionData`/`selectedCard` or
          // used as a React key — not just masked at render time.
          const raw = data as SecurityQuestionsData;
          // Issue #345 (mirrors #282/EDS High): JointAccounts is authoritatively
          // sourced from /security/joint-associations (the real gateway op) —
          // NEVER from this mock-only security-questions op (which the real
          // gateway 500s, issue #76). Strip it here so a stale/legacy value
          // cannot satisfy the joint-accounts check if the authoritative call
          // fails (fail-closed, no-bypass). Preserve any value the
          // authoritative effect below has already set.
          const next = { ...raw };
          delete next.JointAccounts;
          setQuestionData((prev) => ({
            ...next,
            // #527/CCF-107: the BFF enriches with the FULL 16-digit number
            // (dylan219's sign-off); do NOT mask at ingestion — the agent reads
            // the full number back to the customer.
            ActiveVisaDebitCards: raw.ActiveVisaDebitCards ?? [],
            ...(prev.JointAccounts !== undefined
              ? { JointAccounts: prev.JointAccounts }
              : {}),
          }));
          setQuestionsLoaded(true);
          // Stamp the context this data belongs to (CCF-112, #477) so the
          // terminal Security Result event can only fire once THIS customer's
          // own data has loaded — never against a stale prior-customer outcome.
          setQuestionsContext(`${customerNumber}|${tellerId}`);
        }
      } catch {
        // Non-fatal: labels fall back to "Not on file".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  // Joint accounts (CCF-107, issue #345/#282): sourced from the REAL gateway
  // `customer/joint-associations` op (not the mock-only security-questions op,
  // which the real PTSB gateway 500s — issue #76/#282), mirroring EDS High's
  // identical fix (`SecurityScreen.tsx`'s `asksJointAccounts` effect). Clears
  // any prior/legacy value up-front so that if this call fails or returns
  // nothing, the question falls back to "No joint accounts on file" rather
  // than a stale pass (fail-closed, no-bypass).
  useEffect(() => {
    let cancelled = false;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    setQuestionData((prev) =>
      prev.JointAccounts === undefined
        ? prev
        : { ...prev, JointAccounts: undefined },
    );
    setJointAccountsError(false);
    // ai-review HIGH (issue #345 follow-up): re-arm the pending gate on every
    // (re-)run BEFORE the request goes out, so the Pass/Fail control is
    // disabled for the WHOLE window the lookup is in flight — not just once
    // it has failed.
    setJointAccountsResolved(false);
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/joint-associations',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        // Non-OK / no-data is an UPSTREAM ERROR, not an authoritative empty
        // result — it must fail closed (jointAccountsError blocks Pass below),
        // never silently render as "No joint accounts on file".
        if (!response.ok || !data) {
          setJointAccountsError(true);
          setJointAccountsResolved(true);
          return;
        }
        const rows = data.JointAssociations ?? [];
        const formatted = rows.map((r) => {
          const acct = r.AccountLast4 ? `••${r.AccountLast4}` : '';
          const desc = r.AccountDescription ? `${r.AccountDescription} ` : '';
          const holder = r.HolderName ? ` — ${r.HolderName}` : '';
          return `${desc}${acct}${holder}`.trim();
        });
        setQuestionData((prev) => ({ ...prev, JointAccounts: formatted }));
        setJointAccountsResolved(true);
      } catch {
        if (!cancelled) {
          setJointAccountsError(true);
          setJointAccountsResolved(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  // #676 (CCRS-3493): fetch the customer's ACTUAL other products/accounts for
  // the "Do you have any other products or accounts with PTSB?" fallback shown
  // when the maiden-name "not on file" branch is ticked — REUSES
  // `/security/products-summary` (the same real `customer/accounts/list` +
  // `customer/products/list` gateway op EDS High + the Dashboard already use; no
  // new gateway call), rendered as discrete list items with each product's
  // description + last-4 digits (e.g. "Explore Account ••2381"), replacing the
  // old hard-coded generic categories. Only fetched once the branch is ticked —
  // not eagerly on every screen load. A non-OK response or a thrown error moves
  // the state to `'error'` (NOT silently back to `'idle'`), so the screen shows
  // an explicit "could not retrieve" message rather than a made-up script the
  // agent could rubber-stamp a Pass against (mirrors EDS High, ai-review HIGH
  // #346/#347).
  useEffect(() => {
    let cancelled = false;
    if (!mmnNotOnFileValid) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    setOtherProducts('loading');
    otherProductsContextRef.current = null;
    const requestContext = { customerNumber, tellerId };
    void (async () => {
      try {
        const { data, response } = await api.POST(
          '/security/products-summary',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        // ai-review HIGH (#676): `ProductsUnavailable` means the OTHER-products
        // read FAILED and `Products` may be incomplete (accounts-only). Rendering
        // it as an empty array shows the authoritative "No other products or
        // accounts on file." — a fabricated negative the agent could rubber-stamp
        // a Pass against. Treat it as the explicit `'error'` state instead, the
        // same fail-closed handling as a non-OK response (so the screen shows
        // "Unable to retrieve …" and Pass stays blocked).
        if (!response.ok || !data || data.ProductsUnavailable) {
          setOtherProducts('error');
          return;
        }
        // ai-review MEDIUM (#676): a malformed / non-array `Products` payload
        // must fail CLOSED to `'error'`, NOT silently degrade to an empty list
        // (which renders the authoritative "No other products or accounts on
        // file." the agent could rubber-stamp a Pass against). `?? []` only
        // guards null/undefined; type-guard the shape explicitly.
        if (!Array.isArray(data.Products)) {
          setOtherProducts('error');
          return;
        }
        otherProductsContextRef.current = requestContext;
        setOtherProducts(data.Products);
      } catch {
        if (!cancelled) setOtherProducts('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mmnNotOnFileValid, customerNumber, tellerId]);

  // NOTE: high verification is COMMITTED on the explicit "Continue to dashboard"
  // action (see the CTA handler), NOT speculatively in an effect on
  // `allChecksPass`. This avoids leaving the shared customer context flagged
  // `high` if the agent changes an answer to Fail (or toggles paths) after the
  // checks momentarily passed — i.e. verification state is never granted then
  // left stale/un-revoked.

  /** Commit ECL high verification and proceed to the dashboard. */
  async function completeAndContinue(): Promise<void> {
    if (!allChecksPass) return;
    // Record `high` server-side (issue #363) so the high-security servicing
    // writes (SCRN-26 Mobile Number maintenance) are unlocked at the BFF, not
    // just in the client. The client flag drives UX; the server level is the
    // boundary.
    // When 2FA was not performed but the CCF-107 fallback questions were used
    // ("Unable to complete 2FA"), pass a sanctioned skip reason so the BFF can
    // record `high` via the sanctioned-skip path (no server OTP was performed).
    const { ok: recorded } = await commitHighSecurity(
      customerNumber,
      tellerId,
      unableTo2fa
        ? 'Unable to complete 2FA (ECL fallback questions)'
        : undefined,
    );
    if (!recorded) {
      // FAIL CLOSED (issue #363): the BFF did not record `high` (403 / network
      // error). Do NOT flip the client to high or navigate — the high-security
      // servicing writes stay gated at the server and the UX must reflect that.
      toast.error(
        'High Security could not be recorded. Please complete the checks again.',
      );
      return;
    }
    // #427: name the ECL flow in the banner ("ECL High Security Verified").
    updateCustomer({
      securityLevel: 'high',
      customerVerification: 'Y',
      verificationVariant: 'ECL',
    });
    toast.success('Customer verified (ECL high security).');
    navigate('/dashboard');
  }

  /**
   * Handles the recurring-payment question's "Check Transactions" button
   * (issue #345, mirroring dylan219's #344 ruling for EDS High): snapshots the
   * in-progress check state so it survives the same-tab navigation, then
   * navigates to Transactions — carrying `fromSecurity` + `returnTo` so that
   * screen shows a "Back to High Security" button that resumes this run.
   */
  function handleCheckAction(): void {
    saveEclHighRunSnapshot({
      runKey: securityRunKey(customerNumber, ECL_HIGH_SCREEN_ID),
      fullNameStatus,
      dobStatus,
      mmnStatus,
      mobileStatus,
      recurringStatus,
      jointStatus,
      mmnNotOnFile,
      mobileNotOnFile,
      otpSent,
      maskedMobile,
      otpStatus,
      otpAttemptsRemaining,
      otpExpired,
      otpBlocked,
      unableTo2fa,
      fallbackStatus,
      selectedCard,
      // #405: the terminal fallback-failure latch must survive the round trip,
      // or switching back to 2FA after the return would rescue a failed run.
      failedViaFallback,
    });
    navigate('/transactions', {
      state: { fromSecurity: true, returnTo: ECL_HIGH_ROUTE },
    });
  }

  // When the agent toggles the fallback path, reset the OTHER path's result so a
  // stale pass on the abandoned path can't survive (CCF-107: the agent works one
  // path at a time). Switching back into 2FA clears the fallback answers; opting
  // into the fallback clears any prior OTP pass/fail. TERMINAL failures are never
  // cleared by a path toggle: unticking while a fallback answer is Fail latches
  // `failedViaFallback` (#405, narrowed by #840) and keeps the failed answers.
  function handleUnableTo2faChange(next: boolean): void {
    setUnableTo2fa(next);
    if (next) {
      if (otpStatus !== 'fail' && !otpBlocked) setOtpStatus(null);
    } else if (anyFallbackFailed) {
      setFailedViaFallback(true);
    } else {
      setFallbackStatus({ landline: null, email: null, visa: null });
      setSelectedCard('');
    }
  }

  async function handleGenerateOtp(): Promise<void> {
    if (!ensureContext()) return;
    setGenerating(true);
    try {
      const { data, error, response } = await api.POST(
        '/security/otp/generate',
        {
          params: bankHeaderParams(),
          body: { customerNumber },
        },
      );
      if (response.status === 403) {
        // #788 (CCF-16) — a terminal OTP lockout (#639) fails closed with
        // 403 `{ error: 'otp_locked_out' }`. Route it to the dedicated inline
        // CCF-16 block alert (not the generic RBAC/teller toast).
        if (isOtpLockedOut(response.status, error)) {
          setOtpBlocked(true);
          return;
        }
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { sent: false }) as OtpGenerateResult;
      if (body.sent) {
        setOtpSent(true);
        setMaskedMobile(body.maskedMobile ?? '');
        setOtpStatus(null);
        setOtpCode('');
        setOtpExpired(false);
        setOtpBlocked(false);
        setOtpError(null);
        setOtpAttemptsRemaining(body.attemptsRemaining ?? MAX_OTP_ATTEMPTS);
        toast.success(
          body.maskedMobile
            ? `Code sent to ${body.maskedMobile}`
            : 'One-time passcode sent',
        );
      } else if (body.blocked) {
        setOtpBlocked(true);
        toast.error(
          body.message ??
            'OTP generation is blocked for 24 hours after 3 failed attempts.',
        );
      } else {
        // Send Code (generate) failed — surface the gateway code + message
        // INLINE under the Send Code button (#782), not only as a toast.
        const message = body.message ?? 'Could not send the one-time passcode.';
        setOtpError({ code: body.resultCode, message, source: 'generate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'generate' });
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidateOtp(): Promise<void> {
    if (!ensureContext()) return;
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setValidating(true);
    // Clear any prior inline warning at the start of a fresh validate attempt.
    setOtpError(null);
    try {
      const { data, response } = await api.POST('/security/otp/validate', {
        params: bankHeaderParams(),
        body: { customerNumber, otp: otpCode },
      });
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { valid: false }) as OtpValidateResult;
      if (body.valid) {
        setOtpStatus('pass');
        setOtpError(null);
        toast.success('Two-factor authentication passed.');
        return;
      }
      if (typeof body.attemptsRemaining === 'number') {
        setOtpAttemptsRemaining(body.attemptsRemaining);
      }
      if (body.expired) {
        // CCF-107: the 5-minute window elapsed — issue a new OTP (retryable, not
        // a security failure). The expired branch renders its own dedicated
        // inline alert — clear the generic wrong-code warning so they don't
        // double up. #810: prefer the BFF-mapped ruled copy.
        const expiredMessage =
          body.message ??
          'The one-time passcode has expired. Generate a new code.';
        setOtpExpired(true);
        setOtpSent(false);
        setOtpError(null);
        setOtpExpiredMessage(expiredMessage);
        toast.error(expiredMessage);
      } else if (body.blocked) {
        // CCF-107: 3 incorrect attempts — only NOW does 2FA fail (this drives
        // the security-failed/referral guidance via `anyFailed`). The blocked
        // branch has its own dedicated inline alert — clear the generic
        // wrong-code warning so they don't double up.
        setOtpStatus('fail');
        setOtpBlocked(true);
        setOtpSent(false);
        setOtpError(null);
        toast.error(
          body.message ??
            'Incorrect code 3 times. Security has failed — advise the customer to call again.',
        );
      } else {
        // A non-final wrong code: allow retry (do NOT set otpStatus to 'fail',
        // which would prematurely trigger the security-failed branch). Surface
        // the gateway code + message INLINE under the Send Code button as a
        // non-blocking warning (issue #782, per #80/#82/#94), KEEPING the toast.
        const remaining = body.attemptsRemaining;
        const message =
          body.message ??
          (typeof remaining === 'number'
            ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
            : 'The code does not match. Please retry.');
        setOtpError({ code: body.resultCode, message, source: 'validate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'validate' });
      toast.error(message);
    } finally {
      setValidating(false);
    }
  }

  function setFallback(key: FallbackKey, v: 'pass' | 'fail'): void {
    setFallbackStatus((s) => ({ ...s, [key]: v }));
  }

  /** Renders the in-order skip / "answer the earlier question" note for a
   * fallback question that is not (yet) the active one. */
  function fallbackSkipNote(
    key: FallbackKey,
    available: boolean,
    noun: string,
  ): React.JSX.Element {
    if (!available) {
      return (
        <p
          className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
          role="note"
        >
          {`No ${noun} on file — skip to the next question.`}
        </p>
      );
    }
    // Available but not in the active slice ⇒ an earlier question must be
    // answered first (no cherry-picking).
    return (
      <p
        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
        role="note"
      >
        Answer the earlier available question first.
      </p>
    );
  }

  const otpFieldClasses =
    "px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]";
  const primaryBtn =
    "bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]";
  const checkboxClasses =
    'size-[24px] rounded-[4px] border-2 border-[#343f41] accent-[#FF703E]';

  return (
    <main
      className="max-w-[900px] pl-12 pr-12 py-12"
      aria-labelledby="ecl-high-heading"
    >
      <div className="mb-6 flex items-start justify-between gap-8 flex-wrap">
        <h1
          id="ecl-high-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
        >
          ECL: High Security
        </h1>
        {/* High security pages cross-navigate to the OTHER high security pages
            (issue #397 — @dylan219 option A, reverses #101/#117). Pure
            navigation — each target is an existing RBAC-gated route, re-checked
            on arrival (deny-by-default). */}
        <SecurityProcessMenu currentId="SCRN-55" mode="high" />
      </div>

      <div className="space-y-6">
        {/* Mandatory identity questions (CCF-107). */}
        <InfoBox>
          <div className="space-y-6">
            <IdentityQuestion
              label="Full name:"
              value={hasName ? fullName : 'Not available'}
              name="full-name"
              status={fullNameStatus}
              onChange={setFullNameStatus}
            />
            <div className="border-t-2 border-[#dad7d3]" />
            <IdentityQuestion
              label="Date of birth:"
              value={dobDisplay}
              name="dob"
              status={dobStatus}
              onChange={setDobStatus}
            />
            <div className="border-t-2 border-[#dad7d3]" />

            {/* Mother's maiden name — with the "not on file" branch (CCF-107). */}
            <div className="flex items-start gap-4">
              <div className="flex flex-col">
                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                  Mother&apos;s maiden name:
                </h3>
                <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
                  {hasMothersMaidenName ? mothersMaidenName : 'Not on file'}
                </p>
                {/* #626 (@dylan219): the maiden "not on file" tick is ALWAYS
                    offered — even when a (possibly junk) value is on file —
                    because branches enter unmatchable junk into the maiden
                    field, so the agent must always be able to flag it and verify
                    against the customer's other products. The on-file Pass/Fail
                    (below) is still shown too; the agent decides which to use. */}
                {questionsLoaded && (
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="mmn-not-on-file"
                      checked={mmnNotOnFile}
                      onChange={() => {
                        setMmnNotOnFile((v) => !v);
                        setMmnStatus(null);
                        // #676: untick/re-tick must drop any prior resolved
                        // list — the effect refetches on the (re)ticked branch;
                        // this guarantees no window with a stale value shown.
                        setOtherProducts('idle');
                        otherProductsContextRef.current = null;
                      }}
                      className={checkboxClasses}
                    />
                    <label
                      htmlFor="mmn-not-on-file"
                      className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                    >
                      {/* #674 (CCRS-3491): align to EDS High Security wording. */}
                      Tick if Mother&rsquo;s maiden name is blank, TBC, NA,
                      unknown, not known etc.
                    </label>
                  </div>
                )}

                {mmnNotOnFileValid && (
                  <div className="mt-4">
                    <div className="border-t border-[#e5e7eb] mb-4" />
                    <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                      Do you have any other products or accounts with PTSB?
                    </p>
                    {/* #676 (CCRS-3493): the customer's ACTUAL associated
                        products/accounts (description + last-4 digits), sourced
                        from `/security/products-summary` (the same real gateway
                        op EDS High uses — no new call), replacing the old
                        hard-coded generic categories. An `'error'` state renders
                        an explicit "could not retrieve" message rather than a
                        made-up script the agent could rubber-stamp. */}
                    {Array.isArray(otherProductsForContext) ? (
                      otherProductsForContext.length > 0 ? (
                        <>
                          <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                            Customer&apos;s associated products/accounts:
                          </p>
                          <ul className="list-none m-0 p-0 mb-4 space-y-1">
                            {otherProductsForContext.map((p, idx) => (
                              <li
                                key={`${p.AccountLast4}-${idx}`}
                                className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                              >
                                {formatProductEntry(p)}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                          No other products or accounts on file.
                        </p>
                      )
                    ) : otherProductsForContext === 'error' ? (
                      <p
                        role="alert"
                        className="text-[14px] font-['Arial',sans-serif] text-[#c02020] mb-3"
                      >
                        Unable to retrieve the customer&apos;s product list from
                        the bank systems. Mark Fail if this cannot be confirmed
                        by another means.
                      </p>
                    ) : (
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3">
                        Loading the customer&apos;s products…
                      </p>
                    )}
                  </div>
                )}
              </div>
              {(mmnNotOnFile
                ? mmnNotOnFileValid && otherProductsAuthoritative
                : hasMothersMaidenName) && (
                <div className="ml-auto mt-5">
                  <PassFailRadios
                    name="mmn"
                    value={mmnStatus}
                    onChange={setMmnStatus}
                  />
                </div>
              )}
            </div>

            <div className="border-t-2 border-[#dad7d3]" />

            {/* Mobile number — with the "not on file" branch (CCF-107). */}
            <div className="flex items-start gap-4">
              <div className="flex flex-col">
                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                  Mobile number:
                </h3>
                <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-3">
                  {/* Issue #387: show the customer's registered mobile (from
                      customer/info) so the agent can read it back. Prefer the
                      full on-file number; fall back to the masked mobile once
                      the OTP has been generated; only if NEITHER is on file does
                      the empty-state placeholder remain.
                      Issue #677 (CCRS-3499): render that empty state as
                      "Not on file" (was "Confirm with the customer") per the
                      figma — a genuine no-mobile state, not an instruction. */}
                  {mobile || maskedMobile || 'Not on file'}
                </p>
                {/* #701: the tick is always shown (mirrors SecurityScreen.tsx #662 / CCRS-3447). The
                    no-bypass invariant is preserved: when ticked, `requiredFallbackAnswers` becomes
                    2 so the agent must answer 2 alternative questions — the tick alone is not sufficient. */}
                {questionsLoaded && (
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="mobile-not-on-file"
                      checked={mobileNotOnFile}
                      onChange={() => {
                        setMobileNotOnFile((v) => !v);
                        setMobileStatus(null);
                      }}
                      className={checkboxClasses}
                    />
                    <label
                      htmlFor="mobile-not-on-file"
                      className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
                    >
                      {/* #674 (CCRS-3491): align to EDS High Security wording. */}
                      Tick if mobile number not on file
                    </label>
                  </div>
                )}

                {/* Issue #677 (CCRS-3499): when "mobile number not on file" is
                    ticked the mobile question is no longer answerable — per the
                    figma the agent sees the instruction to complete the
                    alternative questions instead, and the Pass/Fail radios are
                    hidden (mobile is excluded from the mandatory checks and its
                    verification defers to the fallback questions, whose pass
                    threshold `mobileNotOnFileValid` already raises to 2). This
                    supersedes the #345 landline-substitute-with-radios display;
                    the fallback question set (landline / email / active Visa
                    debit card) below still enforces the substitute check. */}
                {mobileNotOnFileValid && (
                  <div className="mt-4">
                    <div className="border-t border-[#e5e7eb] mb-4" />
                    <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                      Ensure alternative questions below are completed
                    </p>
                  </div>
                )}
              </div>
              {/* Radios hidden once "not on file" is ticked (#677 / figma). */}
              {!mobileNotOnFileValid && (
                <div className="ml-auto mt-5">
                  <PassFailRadios
                    name="mobile"
                    value={mobileStatus}
                    onChange={setMobileStatus}
                  />
                </div>
              )}
            </div>
          </div>
        </InfoBox>

        {/* Recurring payments + joint accounts (CCF-107). */}
        <InfoBox>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex flex-col">
                <h3 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                  Do you have any recurring payments that go out of your account
                  each month?
                </h3>
                <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-1">
                  If Yes — confirm one payment name and how often it goes out.
                </p>
                <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-3">
                  If No — check the account history for the last two months.
                </p>
                {/* Issue #345 (dylan219): "Check Transactions" behaves exactly
                    as it does on EDS High Security (issue #344) — same-tab
                    navigation to Transactions with a "Back to High Security"
                    link there, restoring this run's in-progress state on
                    return (`handleCheckAction`). */}
                <div className="mb-2">
                  <Button
                    type="button"
                    onClick={handleCheckAction}
                    aria-label="Check customer transactions"
                    className="bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-md px-4 py-2 h-auto font-['Arial',sans-serif] font-bold text-[14px]"
                  >
                    Check Transactions
                  </Button>
                </div>
                {recurringPayment && (
                  <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                    {recurringPayment}
                  </p>
                )}
              </div>
              <div className="ml-auto mt-5">
                <PassFailRadios
                  name="recurring"
                  value={recurringStatus}
                  onChange={setRecurringStatus}
                />
              </div>
            </div>

            <div className="border-t border-[#e5e7eb]" />

            <div className="flex items-start gap-4">
              <div className="flex flex-col">
                <h3 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                  Have you any joint accounts held in PTSB?
                </h3>
                <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-3">
                  If Yes — confirm the joint account holder&apos;s name. If No —
                  accepted as correct.
                </p>
                {jointAccountsError ? (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#c1272d] m-0"
                    role="alert"
                  >
                    Joint accounts could not be verified — this question cannot
                    be marked Pass until the lookup succeeds.
                  </p>
                ) : !jointAccountsResolved ? (
                  // Issue #345 (ai-review HIGH follow-up): a distinct pending
                  // state — never the "no joint accounts" empty state — while
                  // the authoritative lookup is still in flight, so it cannot
                  // be mistaken for an authoritative answer.
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                    role="status"
                  >
                    Checking joint accounts…
                  </p>
                ) : hasJointAccounts ? (
                  // `role="list"`/`role="listitem"` are required here:
                  // `list-style` is an INHERITED CSS property, so Tailwind's
                  // `list-none` on the `<ul>` also computes to `list-style:
                  // none` on each `<li>` — and Chromium strips the implicit
                  // "list"/"listitem" ARIA roles from BOTH the container and
                  // its items once `list-style` is `none`, so without
                  // explicit roles on both, `getByRole('listitem', …)` finds
                  // nothing even though the text renders correctly (issue
                  // #345 — role="list" on the `<ul>` alone was not enough).
                  <ul className="list-none m-0 p-0 space-y-1" role="list">
                    {jointAccounts.map((acct) => (
                      <li
                        key={acct}
                        role="listitem"
                        className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
                      >
                        {acct}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                    role="note"
                  >
                    No joint accounts on file.
                  </p>
                )}
              </div>
              <div className="ml-auto mt-5">
                {/* #817 (@dylan219): the joint-accounts question ALWAYS shows
                    Pass/Fail — even when there are genuinely no joint accounts
                    on file ("No joint accounts on file." above) — superseding
                    the #674/#660 "Not applicable" treatment. The radios stay
                    disabled while the authoritative lookup is in flight or has
                    errored (fail-closed). */}
                <PassFailRadios
                  name="joint"
                  value={jointStatus}
                  onChange={setJointStatus}
                  disabled={jointAccountsError || !jointAccountsResolved}
                />
              </div>
            </div>
          </div>
        </InfoBox>

        {/* 2FA OTP step — to the registered mobile (CCF-107). Disabled once the
            agent has opted into the fallback path. */}
        <InfoBox className="border-l-[#FF703E] border-l-[6px]">
          <div className="space-y-2">
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              {firstName
                ? `Thanks ${firstName}, I need to do one more security check. I'll send a code to your mobile phone.`
                : `Thanks, I need to do one more security check. I'll send a code to your mobile phone.`}
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              Can you tell me your phone number please?
            </p>
            <div className="flex items-baseline gap-2 mb-4 mt-2">
              <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                Customer mobile number:
              </h3>
              {/* CCF-107 / #533: show the on-file mobile BEFORE send-code —
                  the agent reads it out and confirms the customer can receive
                  an OTP on it — from the already-fetched /security/questions
                  (`questionData.MobileNumber`, the same source as the
                  `mobile`/`mobileOnFile` logic above). Once a code has been
                  sent, prefer the (possibly differently-formatted)
                  `maskedMobile` the gateway echoed back on
                  `/security/otp/generate`. Parity with the shared Security
                  screen. */}
              {(maskedMobile ||
                (questionsLoaded && questionData.MobileNumber)) && (
                <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  {maskedMobile || questionData.MobileNumber}
                </p>
              )}
            </div>

            {/* Issue #345 (dylan219): a warning in the 2FA section when "mobile not on file"
                is ticked — OTP cannot be sent; the agent must use the alternative questions.
                #701: text updated — "mobile not on file" can be ticked even when mobile is
                present (agent choosing the alternative path), so "No mobile number is on file"
                would be factually wrong in that case. */}
            {mobileNotOnFileValid && (
              <p
                role="alert"
                className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] mt-0 mb-4"
              >
                OTP cannot be sent. Tick &quot;Unable to complete 2FA&quot;
                below to use the alternative verification questions.
              </p>
            )}

            <div className="mt-4">
              <Button
                type="button"
                onClick={() => void handleGenerateOtp()}
                disabled={
                  generating ||
                  otpStatus === 'pass' ||
                  otpBlocked ||
                  unableTo2fa
                }
                aria-label="Generate one-time passcode"
                className={primaryBtn}
              >
                {generating
                  ? 'Generating…'
                  : otpSent
                    ? 'Resend OTP'
                    : 'Send Code'}
              </Button>
            </div>

            {/* Send Code (generate) failure — gateway code + message shown
                INLINE directly under the Send Code button (#782), so the error
                is obvious right where the agent clicked. Validate failures
                render under the Confirm button instead (below). */}
            {otpError &&
              otpError.source === 'generate' &&
              !unableTo2fa &&
              otpStatus !== 'pass' &&
              !otpExpired &&
              !otpBlocked && (
                <p
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  {otpError.code
                    ? `(${otpError.code}) ${otpError.message}`
                    : otpError.message}
                </p>
              )}

            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-4 mb-4">
              I just sent the code to your phone. When you get it, can you read
              it back to me?
            </p>

            <label
              htmlFor="otp-code"
              className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
            >
              Verification number
            </label>

            <div className="flex items-center gap-4 flex-wrap">
              <input
                id="otp-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="Enter verification number"
                value={otpCode}
                disabled={!otpSent || otpStatus === 'pass' || unableTo2fa}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
                }
                style={{ width: '220px' }}
                className={otpFieldClasses}
              />

              <Button
                type="button"
                onClick={() => void handleValidateOtp()}
                disabled={
                  !otpSent ||
                  otpCode.length !== 6 ||
                  validating ||
                  otpStatus === 'pass' ||
                  unableTo2fa
                }
                aria-label="Validate one-time passcode"
                className={primaryBtn}
              >
                {validating ? 'Validating…' : 'Confirm'}
              </Button>

              {/* Overall security outcome: the flag reflects the WHOLE
                  verification — PASS only when every identity check AND the
                  second factor (OTP or, on the "Unable to complete 2FA"
                  fallback path, the required fallback answers) pass, FAIL if
                  any Pass/Fail question fails or the second factor fails
                  (computed LIVE via anyFailed, #840 — correcting a mis-clicked
                  identity-question or fallback-answer Fail back to Pass
                  recovers the run; a genuine OTP lockout, or a fallback Fail
                  escaped by unticking the fallback (latched per #405), stays
                  failed). It must not represent the 2FA
                  result alone. Issue #345 (dylan219): shown on BOTH paths —
                  if the agent skips 2FA via the fallback and completes every
                  question, the badge must still appear here. */}
              {allChecksPass && (
                <span
                  className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                  aria-label="Verification passed"
                >
                  Pass
                </span>
              )}
              {/* #263: only presented once every step (incl. the second
                  factor) is done. */}
              {showFail && (
                <span
                  className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                  aria-label="Verification failed"
                >
                  Fail
                </span>
              )}
            </div>

            {/* Validate (Confirm) failure — a wrong (non-final) code: gateway
                code + message shown INLINE directly under the Confirm button
                (#782), where the agent just clicked. Stays retryable. The
                expired / blocked branches render their own alerts below. */}
            {otpError &&
              otpError.source === 'validate' &&
              !unableTo2fa &&
              otpStatus !== 'pass' &&
              !otpExpired &&
              !otpBlocked && (
                <p
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  {otpError.code
                    ? `Incorrect code (${otpError.code}): ${otpError.message}`
                    : otpError.message}
                </p>
              )}

            {otpSent &&
              !unableTo2fa &&
              !otpBlocked &&
              !otpExpired &&
              otpStatus !== 'pass' && (
                <p
                  role="status"
                  aria-live="polite"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-3 mb-0"
                >
                  {`OTP attempts remaining: ${otpAttemptsRemaining}`}
                </p>
              )}
            {otpExpired && !unableTo2fa && (
              <p
                role="status"
                className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
              >
                {otpExpiredMessage ??
                  'The one-time passcode has expired. Generate a new code to continue.'}
              </p>
            )}
            {otpBlocked && !unableTo2fa && (
              <p
                role="alert"
                className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
              >
                The one-time passcode was entered incorrectly 3 times. Security
                has failed — advise the customer to call again in 24 hours and
                record the failure.
              </p>
            )}

            {/* Fallback opt-in (CCF-107). #639: once the customer has entered the
                OTP incorrectly on the 3rd attempt (`otpBlocked`), 2FA has
                terminally FAILED — the "Unable to complete 2FA" fallback must
                NOT be tickable (it cannot rescue a failed run). Disable it so
                the screen simply shows the FAIL state, matching the shared
                high-security screen's skip-with-reason control (already disabled
                on `otpBlocked`). */}
            <div className="flex items-center gap-3 mt-6">
              <input
                type="checkbox"
                id="unable-2fa"
                checked={unableTo2fa}
                disabled={otpBlocked}
                onChange={() => handleUnableTo2faChange(!unableTo2fa)}
                className={`${checkboxClasses} disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              <label
                htmlFor="unable-2fa"
                className={`text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] select-none ${otpBlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Unable to complete 2FA
              </label>
            </div>
          </div>
        </InfoBox>

        {/* Fallback questions (CCF-107) — shown when 2FA cannot be completed.
            Asked in the fixed order; the required number of correct answers
            (1 with the mobile on file + confirmed, otherwise 2) passes. */}
        {unableTo2fa && (
          <>
            <div className="bg-[#1D252C] text-white px-6 py-3">
              <h2 className="text-[16px] font-['Arial',sans-serif] font-bold m-0">
                Select the first question that has an available answer. If an
                answer is unavailable, proceed to the next question.
              </h2>
            </div>

            <InfoBox>
              <div className="space-y-6">
                <ul className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 list-disc pl-6">
                  <li className="mb-2">
                    If unable to complete 2FA — 1 question required
                  </li>
                  <li>If mobile number not on file — 2 questions required</li>
                </ul>
                <div className="border-t border-[#e5e7eb]" />

                {/* Q1 — Landline. Blank when not on file → skip to the next. */}
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                      Can you confirm the landline you have on file:
                    </h3>
                    <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                      {landlineAvailable ? landline : 'Not on file'}
                    </p>
                  </div>
                  <div className="ml-auto mt-5">
                    {isFallbackActive('landline') ? (
                      <PassFailRadios
                        name="ecl-landline"
                        value={fallbackStatus.landline}
                        onChange={(v) => setFallback('landline', v)}
                      />
                    ) : (
                      fallbackSkipNote(
                        'landline',
                        landlineAvailable,
                        'landline',
                      )
                    )}
                  </div>
                </div>

                <div className="border-t border-[#e5e7eb]" />

                {/* Q2 — Email. Blank when not on file → skip to the next; a
                    failed lookup is NOT the same as "not on file" (ai-review
                    HIGH, #345 follow-up) — say so explicitly rather than
                    accepting it as an authoritative absence. */}
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                      Can you confirm the email address on file:
                    </h3>
                    {emailAddressError ? (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#c1272d] m-0"
                        role="alert"
                      >
                        Email address could not be verified — this question
                        cannot be used to confirm the customer.
                      </p>
                    ) : (
                      <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                        {emailAvailable ? email : 'Not on file'}
                      </p>
                    )}
                  </div>
                  <div className="ml-auto mt-5">
                    {isFallbackActive('email') ? (
                      <PassFailRadios
                        name="ecl-email"
                        value={fallbackStatus.email}
                        onChange={(v) => setFallback('email', v)}
                      />
                    ) : (
                      fallbackSkipNote('email', emailAvailable, 'email')
                    )}
                  </div>
                </div>

                <div className="border-t border-[#e5e7eb]" />

                {/* Q3 — Active Visa debit card (the catch-all). */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col">
                    <h3 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                      Do you have an active Visa debit card?
                    </h3>
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-1">
                      If yes — confirm the full 16-digit card number.
                    </p>
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-4">
                      If no — this is the correct answer.
                    </p>
                    {activeVisaCardsError ? (
                      // ai-review HIGH (issue #345 follow-up): a failed
                      // card/list lookup is NOT an authoritative "no card" —
                      // say so explicitly rather than accepting it as the
                      // correct answer (this question is excluded from the
                      // fallback threshold above; the agent must rely on the
                      // remaining available fallback question(s)).
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#c1272d] m-0"
                        role="alert"
                      >
                        Active Visa debit card status could not be verified —
                        this question cannot be used to confirm the customer.
                      </p>
                    ) : hasCardOnFile ? (
                      <CardDropdown
                        value={selectedCard}
                        onChange={setSelectedCard}
                        options={activeCards}
                      />
                    ) : (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                        role="note"
                      >
                        No active Visa debit cards on file.
                      </p>
                    )}
                  </div>
                  {orderedAvailableFallback.includes('visa') ? (
                    <div className="ml-auto mt-1">
                      <PassFailRadios
                        name="ecl-visa"
                        value={fallbackStatus.visa}
                        onChange={(v) => setFallback('visa', v)}
                      />
                    </div>
                  ) : (
                    fallbackSkipNote(
                      'visa',
                      !activeVisaCardsError,
                      'Visa debit card',
                    )
                  )}
                </div>

                {/* Insufficient data to meet the required threshold (CCF-107):
                    the customer must visit a branch with photo ID. */}
                {questionsLoaded && !enoughFallbackData && (
                  <div
                    className="bg-[#F8EEE3] border-2 border-[#979C9F] p-4"
                    role="note"
                  >
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                      {`There is not enough data on file to answer the ${requiredFallbackAnswers} question${
                        requiredFallbackAnswers === 1 ? '' : 's'
                      } required. The customer must visit a branch with photo ID and their account details.`}
                    </p>
                  </div>
                )}
              </div>
            </InfoBox>
          </>
        )}

        {/* Branch-referral guidance when a check has failed (CCF-107 unhappy
            path). Driven by showFail — the LIVE anyFailed (#840) so flipping a
            mis-clicked identity-question or fallback-answer Fail back to Pass
            clears it; a genuine OTP lockout (included in anyFailed) or a
            fallback Fail escaped by unticking "Unable to complete 2FA" (latched
            failedViaFallback, #405) keeps it. #263: only presented once
            ALL steps (incl. the second factor) are complete, not on the first
            failed answer while a later step is outstanding. */}
        {showFail && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4 whitespace-pre-line">
                {eclHighFailAdvisory}
              </p>
              {/* Issue #405: when the terminal failure was recorded on the
                  "Unable to complete 2FA" fallback path but the agent has since
                  unticked it (so the failed alternative question is no longer
                  visible), name the cause so the Fail is not unexplained. Driven
                  by the latched `failedViaFallback` (NOT the live state) because
                  the fallback failure is DELIBERATELY terminal (#405) — switching
                  back to 2FA and passing the OTP must not clear it, and the
                  banner must still name the cause. This is the narrowed #840
                  carve-out: identity AND fallback answers recompute live while
                  in use; only a fallback Fail escaped by unticking stays
                  terminal and explained. */}
              {failedViaFallback && !unableTo2fa && (
                <p
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4"
                  role="note"
                >
                  An alternative security question was answered Fail earlier in
                  this check. That result is final for this attempt — re-tick
                  &quot;Unable to complete 2FA&quot; to review it. Passing 2FA
                  afterwards does not clear it; start a new ID&amp;V to try
                  again.
                </p>
              )}
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                {eclHighFailRemark}
              </p>
            </div>
          </div>
        )}

        {/* Continue to Dashboard — gated on the LIVE result of THIS ECL run
            (allChecksPass), not the persisted securityLevel, so it doesn't show
            for an already-high customer or after an answer is changed to Fail. */}
        {allChecksPass && (
          <div className="flex">
            <Button
              type="button"
              onClick={() => void completeAndContinue()}
              aria-label="Continue to dashboard"
              className={primaryBtn}
            >
              Continue to dashboard
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
